import React from 'react';
import Icon from 'components/AppIcon';

const GradientIcon = ({ 
  name, 
  size = 24, 
  gradient = "primary", // primary, secondary, success, warning, error, custom
  customGradient = "",
  className = "",
  containerClassName = "",
  showBackground = true, // Default to true for graduation cap style
  backgroundOpacity = 1, // Full opacity for solid gradient backgrounds
  useWhiteStroke = true, // New prop for white stroke style
  ...props 
}) => {
  const getGradientColors = () => {
    if (gradient === "custom" && customGradient) {
      return customGradient;
    }

    const gradients = {
      primary: { from: "#3b82f6", to: "#8b5cf6" }, // blue to purple
      secondary: { from: "#8b5cf6", to: "#06b6d4" }, // purple to cyan  
      success: { from: "#10b981", to: "#059669" }, // emerald green
      warning: { from: "#f59e0b", to: "#f97316" }, // amber to orange
      error: { from: "#ef4444", to: "#dc2626" }, // red shades
      accent: { from: "#06b6d4", to: "#3b82f6" }, // cyan to blue
      purple: { from: "#a855f7", to: "#ec4899" }, // purple to pink
      blue: { from: "#3b82f6", to: "#06b6d4" }, // blue to cyan
      ocean: { from: "#0ea5e9", to: "#14b8a6" }, // sky blue to teal
      sunset: { from: "#f97316", to: "#ec4899" }, // orange to pink
      forest: { from: "#10b981", to: "#059669" }, // emerald green
      gold: { from: "#f59e0b", to: "#eab308" }, // amber to yellow
      rose: { from: "#f43f5e", to: "#ec4899" }, // rose to pink
      violet: { from: "#8b5cf6", to: "#a855f7" }, // violet shades
      emerald: { from: "#10b981", to: "#059669" }, // emerald green
      sky: { from: "#0ea5e9", to: "#3b82f6" } // sky blue to blue
    };

    return gradients[gradient] || gradients.primary;
  };

  const getBackgroundStyle = () => {
    const colors = getGradientColors();
    return {
      backgroundImage: `linear-gradient(135deg, ${colors.from}, ${colors.to})`,
      opacity: backgroundOpacity
    };
  };

  const iconId = `gradient-icon-${name}-${gradient}-${Math.random().toString(36).substr(2, 9)}`;
  const colors = getGradientColors();

  return (
    <div className={`relative inline-flex items-center justify-center ${containerClassName}`} style={{ minWidth: size, minHeight: size }}>
      {/* Background with gradient */}
      {showBackground && (
        <div 
          className="absolute inset-0 rounded-full"
          style={getBackgroundStyle()}
        />
      )}
      
      {/* Icon with white stroke style or gradient fill */}
      <div className="relative z-10">
        {useWhiteStroke ? (
          <Icon 
            name={name} 
            size={size} 
            className={`text-white ${className}`}
            style={{ 
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
              strokeWidth: '2px'
            }}
          />
        ) : (
          <div style={{ width: size, height: size }}>
            <Icon 
              name={name} 
              size={size} 
              className={`text-transparent ${className}`}
              style={{ 
                fill: `url(#${iconId})`,
                width: '100%',
                height: '100%'
              }}
            />
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              <defs>
                <linearGradient id={iconId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={colors.from} />
                  <stop offset="100%" stopColor={colors.to} />
                </linearGradient>
              </defs>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

// Predefined gradient icon components for common use cases
export const PrimaryGradientIcon = (props) => (
  <GradientIcon gradient="primary" {...props} />
);

export const SuccessGradientIcon = (props) => (
  <GradientIcon gradient="success" {...props} />
);

export const WarningGradientIcon = (props) => (
  <GradientIcon gradient="warning" {...props} />
);

export const ErrorGradientIcon = (props) => (
  <GradientIcon gradient="error" {...props} />
);

// Icon with background container for better visual hierarchy
export const GradientIconContainer = ({ 
  children, 
  gradient = "primary",
  size = "md", // sm, md, lg, xl
  className = "",
  ...props 
}) => {
  const sizeClasses = {
    sm: "w-8 h-8 p-1.5",
    md: "w-12 h-12 p-2.5",
    lg: "w-16 h-16 p-3.5",
    xl: "w-20 h-20 p-4"
  };

  const getGradientStyles = () => {
    const gradients = {
      primary: { 
        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))",
        borderColor: "rgba(59, 130, 246, 0.3)"
      },
      secondary: { 
        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(6, 182, 212, 0.1))",
        borderColor: "rgba(139, 92, 246, 0.3)"
      },
      success: { 
        background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))",
        borderColor: "rgba(16, 185, 129, 0.3)"
      },
      warning: { 
        background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1))",
        borderColor: "rgba(245, 158, 11, 0.3)"
      },
      error: { 
        background: "linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1))",
        borderColor: "rgba(239, 68, 68, 0.3)"
      }
    };
    return gradients[gradient] || gradients.primary;
  };

  const gradientStyle = getGradientStyles();

  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        backdrop-blur-sm border rounded-2xl
        flex items-center justify-center
        shadow-lg hover:shadow-xl transition-all duration-300
        group-hover:scale-110 bg-white
        ${className}
      `}
      style={{
        backgroundImage: gradientStyle.background,
        borderColor: gradientStyle.borderColor
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export default GradientIcon;